import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';
import { isVaultLocked, unlockVault } from '../lib/vaultCrypto';

const {
  FiShield,
  FiLock,
  FiUnlock,
  FiEye,
  FiEyeOff,
  FiCopy,
  FiCheck,
  FiX,
  FiExternalLink,
  FiClock,
  FiAlertTriangle,
} = FiIcons;

function parseCredentialString(raw = '') {
  if (!raw) return { username: '', password: raw || '' };

  const str = raw.trim();
  // Format: "username:password" or "email:password"
  const colonIdx = str.indexOf(':');
  if (colonIdx > 0 && colonIdx < str.length - 1) {
    const candidateUser = str.slice(0, colonIdx).trim();
    const candidatePass = str.slice(colonIdx + 1).trim();
    if (candidateUser && candidatePass) {
      return { username: candidateUser, password: candidatePass };
    }
  }

  // Format: "user=... pass=..." or "username: ... password: ..."
  const userMatch = str.match(/(?:username|user|login|email)\s*[:=]\s*([^\s;,\n]+)/i);
  const passMatch = str.match(/(?:password|pass|secret|pwd)\s*[:=]\s*([^\s;,\n]+)/i);

  if (userMatch || passMatch) {
    return {
      username: userMatch ? userMatch[1].trim() : '',
      password: passMatch ? passMatch[1].trim() : str,
    };
  }

  // Fallback: Entire string is the secret
  return { username: '', password: str };
}

export default function SecureCredentialPopup({
  isOpen,
  onClose,
  item = null,
  themeColors: tc,
  isDark = true,
}) {
  const [revealed, setRevealed] = useState(false);
  const [copiedUser, setCopiedUser] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [pinRequired, setPinRequired] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen && item) {
      playSound('unlock');
      setRevealed(false);
      setCopiedUser(false);
      setCopiedPass(false);
      setTimeLeft(30);

      if (isVaultLocked()) {
        setPinRequired(true);
      } else {
        setPinRequired(false);
      }

      // 30-second Auto-Dismiss security timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  const rawCreds = item.credentials || item.credential || '';
  const { username, password } = parseCredentialString(rawCreds);

  const handleCopyUser = () => {
    if (!username) return;
    playSound('copy');
    navigator.clipboard.writeText(username);
    setCopiedUser(true);
    setTimeout(() => setCopiedUser(false), 2000);
  };

  const handleCopyPass = () => {
    if (!password) return;
    playSound('copy');
    navigator.clipboard.writeText(password);
    setCopiedPass(true);
    setTimeout(() => setCopiedPass(false), 2000);
  };

  const handleUnlockPin = () => {
    if (pinInput.trim().length < 4) {
      setPinError('PIN must be at least 4 digits');
      return;
    }
    const ok = unlockVault(pinInput.trim());
    if (ok) {
      playSound('success');
      setPinRequired(false);
      setPinError('');
    } else {
      playSound('delete');
      setPinError('Incorrect Master PIN');
    }
  };

  const progressPercent = (timeLeft / 30) * 100;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => { playSound('click'); onClose(); }}
          className="fixed inset-0 bg-black/85 backdrop-blur-md"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-md rounded-3xl border shadow-2xl overflow-hidden flex flex-col"
          style={{
            backgroundColor: isDark ? '#080C0A' : '#FFFFFF',
            borderColor: 'rgba(16, 185, 129, 0.4)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.9), 0 0 40px rgba(16, 185, 129, 0.15)',
          }}
        >
          {/* Top Auto-Wipe Countdown Progress Bar */}
          <div className="w-full bg-zinc-800 h-1">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all duration-1000 ease-linear"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b"
            style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center border shadow-sm"
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.15)',
                  borderColor: 'rgba(16, 185, 129, 0.35)',
                }}
              >
                <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold tracking-tight text-white"
                    style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>
                    Sovereign Vault Credential
                  </h3>
                  <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    ISOLATED
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 font-mono">Zero AI Logging • Local Sandbox</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => { playSound('click'); onClose(); }}
              className="w-8 h-8 rounded-xl border flex items-center justify-center text-zinc-400 hover:text-white transition-all cursor-pointer"
              style={{
                backgroundColor: 'rgba(255,255,255,0.04)',
                borderColor: 'rgba(255,255,255,0.1)',
              }}
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Body Content */}
          <div className="p-6 space-y-4">
            {/* Target Bookmark Card */}
            <div
              className="p-3.5 rounded-2xl border flex items-center justify-between gap-3"
              style={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC',
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#E2E8F0',
              }}
            >
              <div className="min-w-0">
                <p className="text-xs font-bold truncate text-white"
                  style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>
                  {item.title || 'Bookmark'}
                </p>
                {item.url && (
                  <p className="text-[11px] text-zinc-400 font-mono truncate mt-0.5">
                    {item.url}
                  </p>
                )}
              </div>

              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-7 h-7 rounded-lg border flex items-center justify-center text-zinc-400 hover:text-emerald-400 border-white/10 shrink-0"
                  title="Open site"
                >
                  <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            {pinRequired ? (
              /* PIN Unlock Enclave */
              <div className="py-4 space-y-3">
                <div className="text-center space-y-1">
                  <div className="w-10 h-10 rounded-full mx-auto flex items-center justify-center bg-amber-500/15 border border-amber-500/30 text-amber-400">
                    <SafeIcon icon={FiLock} className="w-5 h-5" />
                  </div>
                  <h4 className="text-xs font-bold text-white">Vault is Locked with Master PIN</h4>
                  <p className="text-[11px] text-zinc-400">Enter your PIN to reveal saved secret credentials.</p>
                </div>

                <div className="flex gap-2">
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUnlockPin()}
                    placeholder="Enter Master PIN..."
                    className="flex-1 px-3 py-2 rounded-xl border bg-black/40 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#E2E8F0' }}
                  />
                  <button
                    type="button"
                    onClick={handleUnlockPin}
                    className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs cursor-pointer"
                  >
                    Unlock
                  </button>
                </div>
                {pinError && <p className="text-[11px] text-red-400 text-center">{pinError}</p>}
              </div>
            ) : (
              /* Credentials Details Enclave */
              <div className="space-y-3">
                {/* Username / Account */}
                {username && (
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-mono font-semibold text-zinc-400">
                      Username / Email
                    </label>
                    <div
                      className="flex items-center justify-between px-3.5 py-2.5 rounded-xl border font-mono text-xs"
                      style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                        borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                        color: isDark ? '#FFFFFF' : '#0F172A',
                      }}
                    >
                      <span className="truncate">{username}</span>
                      <button
                        type="button"
                        onClick={handleCopyUser}
                        className="p-1 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-all cursor-pointer shrink-0 ml-2"
                        title="Copy username"
                      >
                        <SafeIcon icon={copiedUser ? FiCheck : FiCopy} className={`w-3.5 h-3.5 ${copiedUser ? 'text-emerald-400' : ''}`} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Secret / Password Field */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase font-mono font-semibold text-zinc-400">
                      Secret Password / Token
                    </label>
                    <span className="text-[10px] font-mono text-emerald-400">AES-256 Verified</span>
                  </div>

                  <div
                    className="flex items-center justify-between px-3.5 py-2.5 rounded-xl border font-mono text-xs"
                    style={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                      borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0',
                      color: isDark ? '#FFFFFF' : '#0F172A',
                    }}
                  >
                    <span className="truncate select-all">
                      {revealed ? password : '••••••••••••••••••••'}
                    </span>

                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button
                        type="button"
                        onClick={() => { playSound('click'); setRevealed(!revealed); }}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-all cursor-pointer"
                        title={revealed ? 'Mask password' : 'Peek password'}
                      >
                        <SafeIcon icon={revealed ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={handleCopyPass}
                        className="px-2.5 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 transition-all cursor-pointer text-xs font-semibold"
                        title="Copy password to clipboard"
                      >
                        <SafeIcon icon={copiedPass ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                        <span>{copiedPass ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Security Guarantee Notice */}
                <div
                  className="p-3 rounded-xl border flex items-center gap-2.5 text-[11px] text-zinc-400"
                  style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    borderColor: 'rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>
                    This secret was <strong>never transmitted to OpenAI or AI models</strong> and will self-clear in{' '}
                    <strong className="text-emerald-400">{timeLeft}s</strong>.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-6 py-3 border-t text-[11px] font-mono text-zinc-500"
            style={{
              backgroundColor: isDark ? '#050807' : '#F8FAFC',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
            }}
          >
            <div className="flex items-center gap-1.5">
              <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-zinc-400" />
              <span>Auto-close in {timeLeft}s</span>
            </div>

            <button
              type="button"
              onClick={() => { playSound('click'); onClose(); }}
              className="px-4 py-1.5 rounded-xl border text-xs font-semibold text-zinc-300 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              style={{ borderColor: 'rgba(255,255,255,0.1)' }}
            >
              Close Vault
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
